import { describeWaitReason, knownOutcomeText } from './outcome-text.js';
import { categoryLabel, policyReasonPhrase } from './policy-text.js';
import { netText } from './network-text.js';
/**
 * Слова режима «Тень» (RULINGS-SHADOW) и честные отметки «что сделано»:
 * настоящее действие, сухое (отмечено выполненным, в сеть не уходило) и
 * теневое (алгоритм только решил бы). Никогда не «мы ответили», если в сеть
 * ничего не уходило.
 */
/** Режим «Тень» простыми словами — в «Настройках реагирования». */
export const SHADOW_MODE_DESCRIPTION_RU = [
    'Алгоритм смотрит каждый новый комментарий и решает, что сделал бы в режиме «Авто»: сердечко, ответ (с текстом), скрыть, отдать человеку или ничего.',
    'В Instagram ничего не уходит: ни сердечек, ни ответов, ни скрытий. Решения только записываются — их видно в «Комментариях» (столбец «Алгоритм сделал бы») и в блоке «Тень за период».',
    'Лимиты и прогрев аккаунта тень не тратит. Где решение упёрлось бы в лимит или окно ответов — это тоже видно.',
    'Ставить сердечки и отвечать вручную можно как обычно. При переходе из «Тени» в «Вручную» или «Авто» записанные решения не выполняются.',
];
/** «Тень» простыми словами — с именем сети пары (у Instagram — {@link SHADOW_MODE_DESCRIPTION_RU}). */
export function shadowModeDescription(network) {
    return SHADOW_MODE_DESCRIPTION_RU.map((l) => netText(l, network));
}
/** Подпись решения тени: в сеть не уходило. */
export const SHADOW_NOT_SENT_RU = 'тень — в сеть не отправлено';
/** Подпись сухого действия: отмечено выполненным, в сеть не уходило. */
export const DRY_NOT_SENT_RU = 'сухой режим, не отправлено';
/** Комментарий без действий по решению алгоритма (не человек нажал «Отметить просмотренным»). */
export const ALGO_NO_ACTION_LABEL = 'алгоритм: без действий';
/** Что сделал бы алгоритм (`shadow.kind`) — для столбца и карточки. */
const SHADOW_KIND_RU = {
    like: 'сердечко',
    reply: 'ответ',
    hide: 'скрыть',
    delete: 'удалить',
    manual: 'в ручную очередь',
    none: 'ничего',
};
/**
 * Почему алгоритм так решил: своя фраза к известной причине (категория,
 * причина ручной очереди, код пропуска — у «ничего»), иначе фраза движка,
 * иначе категория, иначе код как есть.
 */
function shadowWhy(s, network) {
    if (s.reason !== null) {
        const known = policyReasonPhrase(s.reason) ?? knownOutcomeText(s.reason, network);
        if (known !== null)
            return known;
        if (s.reasonText !== null)
            return s.reasonText;
    }
    if (s.reasonText !== null)
        return s.reasonText;
    const cat = categoryLabel(s.category);
    if (cat !== null)
        return `это ${cat}`;
    return s.reason;
}
export function describeShadow(s, network) {
    const waitCode = s.wouldWaitReason;
    return {
        what: SHADOW_KIND_RU[s.kind] ?? s.kind,
        text: s.text,
        why: shadowWhy(s, network),
        wouldWait: s.wouldWaitText ?? (waitCode !== null ? describeWaitReason(waitCode, network) : null),
    };
}
/** Короткий текст для строки таблицы: первые `max` символов. */
export function shortText(text, max = 60) {
    return text.length > max ? `${text.slice(0, max)}…` : text;
}
const REAL_TRACE = {
    like: (c) => c.likedByUs,
    reply: (c) => c.repliedByUs,
    hide: (c) => c.hidden === true,
    delete: (c) => c.deleted,
};
const DONE_ORDER = ['like', 'reply', 'hide', 'delete'];
export function doneMarks(c) {
    const out = [];
    for (const kind of DONE_ORDER) {
        const real = REAL_TRACE[kind](c);
        const done = c.done.includes(kind);
        if (!real && !done)
            continue;
        const dry = real ? false : c.doneDry !== null ? c.doneDry.includes(kind) : null;
        out.push({ kind, dry });
    }
    return out;
}
/** Вид выполнен только в сухом режиме (в сеть не уходило). */
export function doneOnlyDry(c, kind) {
    return doneMarks(c).some((m) => m.kind === kind && m.dry === true);
}
/**
 * «Без действий» — кто так решил: человек («Отметить просмотренным») или
 * алгоритм. У нашего комментария и у решённого тенью — подписи нет (там
 * своё объяснение).
 */
export function noActionLabel(c) {
    if (c.state !== 'ignored')
        return null;
    if (c.reviewedBy !== null)
        return 'human';
    if (c.isOwn || c.shadow !== null)
        return null;
    return 'algorithm';
}
/**
 * Строки сводки (RV-SHADOW M4): комментарии и действия — раздельно, чтобы не
 * читалось как «N = сумма». Сердечки/ответы — действия, которые ушли бы
 * сразу (у похвалы бывает и сердечко, и ответ); ручная очередь и «без
 * действий» — комментарии; «упёрлось бы» — действия.
 */
export function shadowSummaryLines(s) {
    return [
        ...(s.comments !== null ? [`Комментариев решено: ${s.comments}`] : []),
        `Действий: сердечек ${s.likes}, ответов ${s.replies}, скрытий ${s.hides}, удалений ${s.deletes}`,
        `В ручную очередь: ${s.manualQueue} комментариев`,
        `Без действий: ${s.noAction} комментариев`,
        `Упёрлось бы в лимиты: ${wouldWaitTotal(s)} действий`,
    ];
}
/** Сколько действий упёрлось бы (движок — `wouldWait`; нет — сумма по причинам). */
export function wouldWaitTotal(s) {
    return s.wouldWait ?? s.wouldWaitByReason.reduce((a, w) => a + w.total, 0);
}
const KIND_GEN_RU = {
    like: 'сердечек',
    reply: 'ответов',
    hide: 'скрытий',
    delete: 'удалений',
};
/**
 * «достигнут лимит … за сутки — 79 (сердечек 72, ответов 7)»: причины по
 * убыванию, фраза движка важнее своей.
 */
export function wouldWaitBreakdown(s, network) {
    return [...s.wouldWaitByReason]
        .filter((w) => w.total > 0)
        .sort((a, b) => b.total - a.total)
        .map((w) => {
        const kinds = DONE_ORDER.filter((k) => w[k] > 0).map((k) => `${KIND_GEN_RU[k]} ${w[k]}`);
        const what = (w.text ?? describeWaitReason(w.reason, network)).replace(/[.\s]+$/, '');
        return { text: kinds.length > 0 ? `${what} (${kinds.join(', ')})` : what, n: w.total };
    });
}
/** «пропустил бы»: причины по убыванию, словами. */
export function skippedBreakdown(s, network) {
    return Object.entries(s.skippedByReason)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([code, n]) => ({ text: knownOutcomeText(code, network) ?? code, n }));
}
/**
 * Остальные действия теневого решения (кроме главного `kind`), которые
 * алгоритм сделал бы или которые упёрлись бы: «и сердечко», «ответ —
 * упёрлось бы: …». Пропущенные и отданные человеку не перечисляются (их
 * объясняет главное решение).
 */
export function shadowExtraActions(s, network) {
    return s.actions
        .filter((a) => a.kind !== s.kind && a.skipReason === null && !a.requiresApproval)
        .map((a) => {
        const what = SHADOW_KIND_RU[a.kind] ?? a.kind;
        const wait = a.wouldWaitText ??
            (a.wouldWaitReason ? describeWaitReason(a.wouldWaitReason, network) : null);
        return wait ? `${what} — упёрлось бы: ${wait}` : what;
    });
}
/** Категории решений по убыванию, словами. */
export function categoryBreakdown(s) {
    return Object.entries(s.byCategory)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([code, n]) => ({ text: categoryLabel(code) ?? code, n }));
}
/** Период сводки тени. */
export const SHADOW_PERIODS = ['today', '24h', 'custom'];
export const SHADOW_PERIOD_LABEL_RU = {
    today: 'Сегодня (с 00:00 по вашему времени)',
    '24h': 'Последние 24 часа',
    custom: 'Выбрать даты',
};
/**
 * Границы периода в ISO. `custom` — дни включительно: от начала первого дня
 * до начала дня после последнего (по времени браузера), но не позже «сейчас».
 * null — период не выбран до конца (нет дат) или пустой.
 */
export function shadowPeriodRange(period, now, custom = { from: undefined, to: undefined }) {
    if (period === '24h') {
        return { from: new Date(now.getTime() - 86_400_000).toISOString(), to: now.toISOString() };
    }
    if (period === 'today') {
        const start = new Date(now);
        start.setHours(0, 0, 0, 0);
        return { from: start.toISOString(), to: now.toISOString() };
    }
    if (!custom.from)
        return null;
    const from = new Date(custom.from);
    from.setHours(0, 0, 0, 0);
    const last = new Date(custom.to ?? custom.from);
    last.setHours(0, 0, 0, 0);
    const end = new Date(last);
    end.setDate(end.getDate() + 1);
    const to = end.getTime() > now.getTime() ? now : end;
    if (from.getTime() >= to.getTime())
        return null;
    return { from: from.toISOString(), to: to.toISOString() };
}
//# sourceMappingURL=shadow-text.js.map